// Host identity-verification state, shared by every surface that mirrors the
// server's host-side gate (venue edit "Submit for review", ExchangeActions
// "Confirm"). One implementation so the client can't drift from itself.
//
// IMPORTANT: /api/verification/me/verification is HAT-SCOPED — the worker
// derives the flow from `activeHat`, and only the 'host' and 'admin' flows
// include the caller's host_user rows. Consumers must not treat a response
// fetched under the artist hat as evidence the host is unverified; gate the
// fetch (or the conclusion) on the active hat and fail open otherwise — the
// server stays the authority.

export type HostVerification = {
  subject_kind: 'artist' | 'host_user' | 'venue';
  state: 'pending' | 'approved' | 'rejected' | 'revoked';
};

export type HostVerificationsResponse =
  | { ok: true; verifications: HostVerification[] }
  | { ok: false; error?: { message?: string } };

export async function fetchHostVerifications(): Promise<HostVerificationsResponse> {
  const r = await fetch('/api/verification/me/verification', {
    credentials: 'include',
  });
  return (await r.json()) as HostVerificationsResponse;
}

/**
 * The host's own identity-verification state. Matches the server gates
 * (exchange confirm, venue liveness), which read only the LATEST host_user
 * row — the API returns rows ordered newest-first per subject, so the first
 * host_user row is that latest row. An older approved row superseded by a
 * newer pending/revoked one must NOT read as approved here while the server
 * blocks (CFR-011).
 */
export function summariseHostVerificationState(
  rows: HostVerification[],
): 'approved' | 'pending' | 'rejected' | 'revoked' | 'none' {
  const latest = rows.find((r) => r.subject_kind === 'host_user');
  return latest ? latest.state : 'none';
}
